import Section from './Section'
import Eyebrow from './Eyebrow'
import { H2 } from './SectionHead'
import PhoneFrame from './PhoneFrame'

// Show the product working, in order, as a sequence of real screens.
//
// The founder’s point (2026-10-02) and it is the right one: this is the first
// thing a visitor sees, and nobody signs up for an adjective. One generic wallet
// card does not demonstrate a marketplace. Three screens in the order they
// actually happen do, because the reader follows a shift from posted to paid and
// works out the product for themselves.
//
// Every phone is a REAL screen of the app (PhoneFrame's AppScreen), captured
// from the apps' own code on an invented cast by scripts/app-screens. No real
// workplace, no real person, and it is the product, not a drawing of it.
//
// Layout: a horizontal rail. Three phones side by side on a wide screen, and on
// a phone it scrolls sideways with the caption under each, which is the one
// pattern that does not either shrink the screens into illegibility or stack
// them into a mile of page.

// Polish (founder 2026-10-05): each step sits in its own framed card, and the
// phone stands on a soft pool of action blue light instead of a bare caption.
// The spec §8 duty to mark money on a mockup as illustrative is met ONCE, by a
// quiet footnote under the rail, not a label under every phone. Pass
// footnote={null} only for a tour whose screens show no money.

export default function ProductTour({
  eyebrow, title, lead, steps, ground = 'navy',
  footnote = 'Screens shown with illustrative figures.',
}) {
  const dark = ground === 'navy'

  return (
    <Section ground={ground}>
      <div className="max-w-[760px]">
        {eyebrow && <Eyebrow on={dark ? 'navy' : 'light'} className="mb-3">{eyebrow}</Eyebrow>}
        <H2 size="xl">{title}</H2>
        {lead && (
          <p className={`mt-6 max-w-[640px] text-[19px] leading-relaxed md:text-[21px] ${dark ? 'text-white/70' : 'text-ink-muted'}`}>
            {lead}
          </p>
        )}
      </div>

      {/* The rail. `-mx-6 px-6` lets it bleed to the screen edge on a phone so the
          third card is visibly cut off, which is what tells a thumb to swipe. */}
      {/* ⚠️ Columns follow the STEP COUNT. Hard coding three left a two step
          tour sitting in two thirds of the row with an empty third, which reads
          as a missing card rather than a deliberate pair. */}
      {/* ⚠️ tabIndex, role and a name. On a phone this is a horizontal
          scroller, and a scroll container that cannot be reached by keyboard is
          content a keyboard user cannot read. Chrome auto-focuses scrollers,
          but it announces as nothing without a name, and Firefox and Safari
          give it no stop at all. */}
      {/* ⚠️ THE SCROLLER IS THE WRAPPER, not the list.
          Putting role="region" straight on the <ol> made it keyboard reachable
          and named, and in doing so REPLACED its list semantics: axe then
          reported 10 orphaned <li> ("listitem") plus 4 "aria-allowed-role",
          because region is not a role an <ol> may take. A fix that trades one
          violation for two is not a fix. The div owns the scrolling, the focus
          stop and the name; the <ol> stays a list. */}
      <div
        tabIndex={0}
        role="region"
        aria-label={`${title} — ${steps.length} steps`}
        className="-mx-6 mt-14 overflow-x-auto px-6 pb-4 md:mx-0 md:mt-20 md:overflow-visible md:px-0"
      >
      <ol
        className="flex snap-x snap-mandatory gap-5 md:grid md:gap-8"
        style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
      >
        {steps.map((s, i) => (
          <li
            key={s.title}
            className={`flex w-[332px] shrink-0 snap-start flex-col overflow-hidden rounded-[28px] md:w-auto ${
              dark
                ? 'bg-gradient-to-b from-navy-surface to-navy-surface/30 ring-1 ring-inset ring-white/[0.07]'
                : 'bg-surface shadow-[0_24px_60px_-32px_rgba(12,24,41,0.25)] ring-1 ring-inset ring-hairline'
            }`}
            style={{ gridColumn: 'span 1' }}
          >
            {/* The stage: a soft pool of action blue light behind the phone and a
                floor shadow under it, so it stands rather than floats. */}
            <div className="relative flex justify-center px-5 pb-10 pt-10 md:pb-12 md:pt-14">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                  background: dark
                    ? 'radial-gradient(70% 60% at 50% 48%, rgba(33,143,183,0.42) 0%, rgba(33,143,183,0.14) 42%, rgba(33,143,183,0) 72%)'
                    : 'radial-gradient(70% 60% at 50% 48%, rgba(33,143,183,0.20) 0%, rgba(33,143,183,0.07) 42%, rgba(33,143,183,0) 72%)',
                }}
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute bottom-[22px] md:bottom-[30px] left-1/2 h-[40px] w-[240px] -translate-x-1/2 rounded-[50%] blur-[18px]"
                style={{ background: dark ? 'rgba(0,0,0,0.55)' : 'rgba(12,24,41,0.18)' }}
              />
              <PhoneFrame className="relative" caption={null}>{s.screen}</PhoneFrame>
            </div>
            <div className={`relative flex-1 border-t px-7 pb-8 pt-7 md:px-9 md:pb-10 md:pt-8 ${dark ? 'border-white/[0.07]' : 'border-hairline'}`}>
              <p className={`text-[12px] font-semibold uppercase tracking-[0.14em] ${dark ? 'text-white/45' : 'text-ink-muted'}`}>
                Step {i + 1}
              </p>
              <h3 className="mt-2 text-[22px] font-semibold tracking-[-0.01em] md:text-[24px]">{s.title}</h3>
              <p className={`mt-2.5 text-[17px] leading-relaxed ${dark ? 'text-white/70' : 'text-ink-muted'}`}>
                {s.body}
              </p>
            </div>
          </li>
        ))}
      </ol>
      </div>

      {footnote && (
        <p className={`mt-8 text-[13px] ${dark ? 'text-white/45' : 'text-ink-muted/80'}`}>{footnote}</p>
      )}
    </Section>
  )
}
